import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, LogOut, Settings } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { UserAvatar } from '@/components/users/UserAvatar';
import { useAuthStore } from '@/store/authStore';
import { useTimezones } from '@/hooks/useTimezones';
import { useSetUserTimezone } from '@/hooks/useSetUserTimezone';
import type { SelectOption } from '@/types/common.types';

interface UserPillMenuProps {
  userName: string;
  userRole: string;
  userAvatarUrl: string | null;
  /** Callback invoked when the user picks "Sign Out". */
  onSignOut: () => void;
  /**
   * Called when a menu entry navigates within the app. Used to close the mobile
   * nav drawer; sign-out is excluded because it leaves the app entirely.
   */
  onNavigate?: () => void;
}

/**
 * Account menu anchored to the bottom-of-sidebar user pill. Houses an
 * inline timezone switcher (legacy parity with the per-user
 * `users_timezones` row) plus quick links to Account Settings and
 * Sign Out.
 *
 * Owns its own trigger `<button>` (rather than wrapping a child via
 * `<PopoverTrigger asChild>`). The previous implementation tried to
 * pass `<SidebarMenuButton>` as the trigger child, but Radix's `Slot`
 * couldn't reliably forward click handlers through that function
 * component, so clicks did nothing. By rendering the button + its
 * visual contents directly here we avoid every layer of `asChild`
 * forwarding ambiguity.
 *
 * Tailwind classes mirror what `SidebarMenuButton size="lg"` produces
 * (`h-12`, padding, rounded, hover/active accent colors) so the pill
 * looks identical in expanded mode. We don't replicate the
 * collapsed-icon-mode tooltip — `aria-label` covers the screen-reader
 * case; in icon mode the avatar still works as the popover trigger.
 */
export function UserPillMenu({
  userName,
  userRole,
  userAvatarUrl,
  onSignOut,
  onNavigate,
}: UserPillMenuProps) {
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const { data: timezoneOptions = [], isLoading: tzLoading } = useTimezones();
  const setTimezoneMutation = useSetUserTimezone();

  // Selected option: prefer the explicit `timezoneID` from the auth
  // store; fall back to reverse-matching the IANA name against the
  // option list's `tzName` for older login payloads that didn't carry
  // the id (the StaffAuthController change rolls forward; this keeps
  // the picker functional during the deploy window).
  const selectedTimezoneId = useMemo<number>(() => {
    if (typeof user?.timezoneID === 'number') return user.timezoneID;
    if (user?.timezone) {
      const match = timezoneOptions.find((tz) => tz.tzName === user.timezone);
      if (match) return match.value;
    }
    return 0;
  }, [user?.timezoneID, user?.timezone, timezoneOptions]);

  const selectOptions = useMemo<SelectOption<number>[]>(
    () => timezoneOptions.map((tz) => ({ value: tz.value, label: tz.label })),
    [timezoneOptions],
  );

  const handleTimezoneChange = (value: number | null) => {
    if (value == null) return;
    if (value === selectedTimezoneId) return;
    setTimezoneMutation.mutate(value);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${userName} account menu`}
          aria-haspopup="dialog"
          aria-expanded={open}
          // Mirrors `sidebarMenuButtonVariants` (size=lg) so the pill
          // visually matches the rest of the sidebar's menu buttons.
          className="peer/menu-button flex h-12 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none transition-colors hover:bg-white/10 active:bg-white/15 focus-visible:ring-2 focus-visible:ring-white/30 group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-0 cursor-pointer"
        >
          <UserAvatar name={userName} photoUrl={userAvatarUrl} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white truncate">{userName}</p>
            {userRole ? (
              <p className="text-[10px] text-white/50 truncate">{userRole}</p>
            ) : null}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" sideOffset={8} className="w-72 p-3">
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              <Globe className="h-3 w-3" />
              <span>Time Zone</span>
              {setTimezoneMutation.isPending && <Spinner size="sm" />}
            </div>
            <Select<number>
              options={selectOptions}
              value={selectedTimezoneId}
              onChange={handleTimezoneChange}
              placeholder={tzLoading ? 'Loading...' : 'Select timezone'}
              disabled={tzLoading || setTimezoneMutation.isPending}
            />
            {setTimezoneMutation.isError && (
              <div className="mt-1 text-[10px] text-rose-600">
                Couldn't update timezone. Try again.
              </div>
            )}
          </div>

          <div className="border-t border-slate-200" />

          <div className="space-y-1">
            <Link
              to="/settings"
              onClick={() => {
                setOpen(false);
                onNavigate?.();
              }}
              className="flex items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
            >
              <Settings className="h-4 w-4 text-slate-500" />
              <span>Account Settings</span>
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
            >
              <LogOut className="h-4 w-4 text-slate-500" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
